import React, {Component} from "react";
import CustomerOfferEntity, {CustomerOfferRuleIds} from "../../data/entities/CustomerOfferEntity";
import CustomerOffersService from "../../data/services/CustomerOffersService";
import {UserContext} from "../../context/UserContext";
import {AxiosResponse} from "axios";
import {ScaleLoader} from "react-spinners";
import {GameEntity} from "../../data/entities/GameEntity";
import {FormattedMessage} from "react-intl";
import RewardsService from "../../data/services/RewardsService";
import update from 'immutability-helper';
import CustomerOffer from "./CustomerOffer";
import RewardEntity from "../../data/entities/RewardEntity";
import ErrorAlert from "../../components/ErrorAlert";
import ErrorMessage from "../../components/ErrorMessage";

interface Props {
    locale: string;
    games: GameEntity[];
}

interface State {
    offers: CustomerOfferEntity[];
    loading: boolean;
    hasError: boolean;
    hasActivationError: boolean;
}

export default class CustomerOffersList extends Component<Props, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    state: State = {
        offers: [],
        loading: true,
        hasError: false,
        hasActivationError: false,
    };

    render(): JSX.Element {
        return (
            <div className="offers-list" id="nav-anchor-rewards">
                {
                    this.state.loading &&
                    <div className="offers-list-loader">
                        <ScaleLoader color="#fff"/>
                    </div>
                }

                {
                    this.state.hasError &&
                    <div className="mt-5">
                        <ErrorMessage
                            message={<FormattedMessage id="message.error"/>}
                            onRefresh={this.refreshCurrentOffers}
                        />
                    </div>
                }

                {
                    this.state.hasActivationError &&
                    <ErrorAlert
                        message={<FormattedMessage id="message.error"/>}
                        onClose={this.closeError}
                    />
                }

                {
                    this.state.offers.map((offer, index) => (
                        <CustomerOffer
                            key={index}
                            locale={this.props.locale}
                            offer={offer}
                            rewardsLeft={this.rewardsLeft(offer)}
                            index={index}
                            games={this.props.games}
                            claimReward={this.claimReward}
                            refreshOffers={this.refreshCurrentOffers}
                            user={this.context.user}
                        />
                    ))
                }
            </div>
        );
    }

    componentDidMount(): void {
        this.getCurrentOffers();
    }

    refreshCurrentOffers = (): void => {
        this.setState({loading: true, offers: [], hasError: false}, this.getCurrentOffers);
    };

    private getCurrentOffers = (): void => {
        if (this.context.user.ticketData.ticket) {
            CustomerOffersService.getCurrentCustomerOffers(
                this.context.user.ticketData.ticket,
                this.props.locale,
            ).then((response: AxiosResponse) => {
                this.setState({
                    offers: response.data.offers,
                    loading: false
                });
            }).catch(() => {
                this.setState({
                    hasError: true,
                    loading: false
                });
            });
        }
    };

    claimReward = (offerIndex: number, rewardIndex: number, callback: (success: boolean) => void): void => {
        if (
            this.context.user.ticketData.ticket &&
            this.state.offers[offerIndex] !== undefined &&
            this.state.offers[offerIndex].rewards[rewardIndex] !== undefined
        ) {
            let success: boolean = false;

            RewardsService.claimReward(this.context.user.ticketData.ticket, this.state.offers[offerIndex].rewards[rewardIndex].id)
                .then(() => {
                    success = true;
                    this.setState(prevState => (update(prevState, {
                        offers: {
                            [offerIndex]: {
                                rewards: {
                                    [rewardIndex]: {
                                        claimed: {$set: true}
                                    }
                                }
                            }
                        }
                    })));
                }).catch(() => {
                    this.setState({hasActivationError: true});
                }).finally(() => callback(success));
        }
    };

    private rewardsLeft = (offer: CustomerOfferEntity): number => {
        let claimedRewardsCount = this.claimedRewardsCount(offer);
        let maxRewardsCount = this.maxRewardsCount(offer);

        return maxRewardsCount - claimedRewardsCount;
    };

    private claimedRewardsCount = (offer: CustomerOfferEntity): number => {
        return offer.rewards.reduce((claimedCount: number, reward: RewardEntity) => {
            return reward.claimed ? ++claimedCount : claimedCount;
        }, 0);
    };

    private maxRewardsCount = (offer: CustomerOfferEntity): number => {
        if (offer.rule === null) {
            return offer.rewards.length;
        }

        switch (offer.rule.id) {
            case CustomerOfferRuleIds.progressionBased:
            default:
                let maxRewardsCount = parseInt(offer.rule.value);
                if (maxRewardsCount > offer.rewards.length) {
                    return offer.rewards.length;
                }

                return maxRewardsCount;
        }
    };

    closeError = () => {
        this.setState({hasActivationError: false});
    };
}